import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import { toast } from 'react-toastify';
import PortConfiguration from '../masters/PortConfiguration'; // Import the configuration component
import ConfirmDialog from '../common/ConfirmDialog'; // Import the custom dialog
import JobForm from '../documentation/JobForm'; // Import JobForm
import { openFilePreview } from '../common/FilePreviewModal';
import { getAuthHeaders } from '../../auth'; // Import the centralized auth function
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import useSortableTable from '../common/useSortableTable';
import TableScrollContainer from '../common/TableScrollContainer';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import DnRoReportTab from './DnRoReportTab';
import '../documentation/JobList.css';

const API_BASE = '/api/documentation/jobs';

const fundRequestIcon = (r) => r.is_fund_request && !r.fund_request_released && (r.port_remarks || '').toUpperCase() !== 'RELEASED' ? (
  <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />
) : null;

const WHARFAGE_OPTIONS = ['PAID', 'NOT PAID', 'GATE OUT'];
const isPreverification = (status) => /PRE[- ]?VERIFICATION/i.test(String(status || ''));

const CopyCell = ({ text, children }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
        if (!val || val === '-') return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(val).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = val;
            ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
            document.body.removeChild(ta);
        }
    };
    return (
        <span className="copy-cell">
            {children}
            <button
                type="button"
                className={`copy-icon-btn${copied ? ' copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                onClick={handleCopy}
                onMouseDown={e => e.stopPropagation()}
            >
                {copied
                    ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                }
            </button>
        </span>
    );
};

const toUpperDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value.toString().toUpperCase();
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return value.toString().toUpperCase();
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getPortRemarksDisplay = (row) => {
  const remark = (row?.port_remarks || '').toString();
  const upper = remark.toUpperCase();
  if (upper !== 'TO RELEASE') return toUpperDisplay(remark);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateCounts = {};
  (row.containers || []).forEach(c => {
    const frd = c.future_release_date;
    if (!frd) return;
    const key = String(frd).split('T')[0].split(' ')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
    const [y, m, d] = key.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    if (isNaN(dateObj) || dateObj < today) return;
    dateCounts[key] = (dateCounts[key] || 0) + 1;
  });

  const sortedKeys = Object.keys(dateCounts).sort();
  if (sortedKeys.length === 0) return 'TO RELEASE';

  const totalContainers = (row.containers || []).length;
  const showCount = totalContainers > 1;

  const formatted = sortedKeys.map((key) => {
    const [, month, day] = key.split('-');
    const count = dateCounts[key];
    const countSuffix = showCount ? `(${count})` : '';
    return `${day}/${month}${countSuffix}`;
  }).join(', ');

  return `TO RELEASE - ${formatted}`;
};

const formatDoStatus = (doStatuses, dropOffExpiryDate) => {
  if (!doStatuses || !Array.isArray(doStatuses) || doStatuses.length === 0) return '-';
  const status = doStatuses[0];
  if (!status) return '-';
  const statusUpper = status.toString().toUpperCase();

  // Trim DEMURRAGE to DEM
  if (statusUpper.includes('DEMURRAGE CONFIRMATION')) {
    return statusUpper.replace('DEMURRAGE CONFIRMATION', 'DEM CONF');
  }

  // Trim CONFIRMATION to CONF
  if (statusUpper.includes('CONFIRMATION')) {
    return statusUpper.replace('CONFIRMATION', 'CONF');
  }

  // Handle DO RECEIVED, AWAITING DROP-OFF
  if (statusUpper === 'DO RECEIVED, AWAITING DROPOFF' || statusUpper === 'DO RECEIVED, AWAITING DROP-OFF') {
    return 'DO RECEIVED';
  }

  // Handle DO RECEIVED with dropoff expiry date
  if (statusUpper === 'DO RECEIVED') {
    if (dropOffExpiryDate) {
      const date = new Date(dropOffExpiryDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `DROP OFF ${day}-${month}-${year}`;
    }
  }

  return statusUpper;
};

// True when the drop-off expiry date is tomorrow, today, or already past (day precision).
const isDropOffExpiringSoon = dateStr => {
  if (!dateStr) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  if (isNaN(d.getTime())) return false;
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  return diff <= 1;
};
const isReleasedRow = r => String(r?.port_remarks || '').toUpperCase().includes('RELEASED');

const TANZANIA_REGIONS = [
    'Arusha', 'Dar es Salaam', 'Dodoma', 'Geita', 'Iringa', 'Kagera', 'Katavi', 'Kigoma', 
    'Kilimanjaro', 'Lindi', 'Manyara', 'Mara', 'Mbeya', 'Morogoro', 'Mtwara', 'Musoma', 'Mwanza', 
    'Njombe', 'Pemba North', 'Pemba South', 'Pwani', 'Rukwa', 'Ruvuma', 'Shinyanga', 
    'Simiyu', 'Singida', 'Songwe', 'Tabora', 'Tanga', 'Zanzibar Central/South', 
    'Zanzibar North', 'Zanzibar Urban/West'
].sort();

// Helper to determine text color based on background
const getTextColorForBackground = (hexColor) => {
    if (!hexColor || hexColor === '#FFFFFF') return '#000000';
    const rgb = parseInt(hexColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 128 ? '#FFFFFF' : '#000000';
};

// --- Container Details Dialog ---
const ContainerDialog = ({ job, transporters, lclTransporters, onClose, onSaveSuccess, hasPermission, readOnly = false }) => { // 2. Receive hasPermission
    // Determine cargo type
    const isVehicle = useMemo(() => (job?.cargo_type || '').toUpperCase() === 'VEHICLE', [job]);
    // LCL is determined if there is no container number or total_fcl is 0
    const isLCL = useMemo(() => {
        if (isVehicle) return false;
        const containers = job?.containers || [];
        const hasContainers = containers.length > 0 && containers.some(c => c.container_no && c.container_no.trim() !== '');
        return !hasContainers;
    }, [job, isVehicle]);

    // Use appropriate transporter list based on cargo type
    const availableTransporters = useMemo(() => {
        if (isVehicle || isLCL) return (lclTransporters || transporters);
        return transporters;
    }, [isVehicle, isLCL, lclTransporters, transporters]);

    const minFutureDate = new Date();
    minFutureDate.setHours(0, 0, 0, 0);
    const minFutureDateStr = `${minFutureDate.getFullYear()}-${String(minFutureDate.getMonth() + 1).padStart(2, '0')}-${String(minFutureDate.getDate()).padStart(2, '0')}`;
    const fmtDateInput = (d) => d ? String(d).split('T')[0].split(' ')[0].split('/').reverse().join('-') : '';


    // Initialize containers with transporter value from the correct field
    const [containers, setContainers] = useState(() => {
        if (isVehicle) {
            const vehicles = job.vehicles || [];
            return vehicles.map(v => ({
                ...v,
                container_no: v.chassis_no,
                transporter: v.transporter_text || ''
            }));
        }
        const initialContainers = job.containers || [];
        let mapped = initialContainers.map(c => ({
            ...c,
            // For LCL, use transporter_text; for FCL, use transporter
            transporter: isLCL ? (c.transporter_text || c.transporter) : c.transporter
        }));
        // If this is an LCL job with no container records, render a single editable row
        if (!isVehicle && isLCL && mapped.length === 0) {
            mapped = [{
                id: 'lcl',
                container_no: '',
                size: '',
                container_type: '',
                seal_no: '',
                kk_seal_no: '',
                release_date: '',
                future_release_date: '',
                transporter: '',
                region: ''
            }];
        }
        return mapped;
    });
    const [selectedContainerIds, setSelectedContainerIds] = useState([]);
    const [bulkTransporter, setBulkTransporter] = useState('');
    const [bulkRegion, setBulkRegion] = useState('');
    const [bulkReleaseDate, setBulkReleaseDate] = useState('');
    const [saving, setSaving] = useState(false);

    const handleContainerChange = (id, field, value) => {
        setContainers(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const applyBulkChanges = () => {
        if (selectedContainerIds.length === 0) return;
        setContainers(prev => prev.map(c => {
            if (selectedContainerIds.includes(c.id)) {
                return {
                    ...c,
                    transporter: bulkTransporter || c.transporter,
                    region: bulkRegion || c.region,
                };
            }
            return c;
        }));
        toast.info('Bulk changes applied. Click "Save Changes" to commit.');
    };

    const applyBulkReleaseDate = () => {
        if (!bulkReleaseDate || selectedContainerIds.length === 0) return;
        setContainers(prev => prev.map(c => {
            if (selectedContainerIds.includes(c.id)) {
                return { ...c, release_date: bulkReleaseDate };
            }
            return c;
        }));
        toast.info('Bulk release date applied. Click "Save Changes" to commit.');
    };


    const handleSelectionChange = (id) => {
        setSelectedContainerIds(prev => 
            prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedContainerIds(containers.map(c => c.id));
        } else {
            setSelectedContainerIds([]);
        }
    };
    

    const handleSave = async () => {
        setSaving(true);
        try {
            if (isVehicle) {
                const payload = containers.map(c => ({
                    id: c.id,
                    kk_seal_no: c.kk_seal_no || '',
                    transporter_text: c.transporter || '',
                    region: c.region || ''
                }));
                const res = await fetch(`${API_BASE}/bulk-update-vehicles/`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error('Failed to save vehicle details.');
                toast.success('Vehicle details saved successfully!');
            } else {
                const payload = containers.map(c => {
                    const isSynthetic = c.id === 'lcl';
                    const base = {
                        kk_seal_no: c.kk_seal_no || '',
                        release_date: c.release_date || null,
                        future_release_date: c.future_release_date || null,
                        region: c.region || null
                    };
                    if (isSynthetic) {
                        return {
                            ...base,
                            job_id: job.id,
                            transporter: null,
                            transporter_text: String(c.transporter || '')
                        };
                    }
                    return {
                        ...base,
                        id: c.id,
                        transporter: isLCL ? null : (c.transporter || null),
                        transporter_text: isLCL ? (c.transporter || null) : null
                    };
                });
                const res = await fetch(`${API_BASE}/bulk-update-containers/`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error('Failed to save container details.');
                toast.success('Container details saved successfully!');
            }
            onSaveSuccess();
        } catch (e) {
            toast.error(e.message);
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="dialog-container" style={{ width: 'auto', maxWidth: '90vw' }}>
                <div className="dialog-header">
                    <h3>{isVehicle ? 'Vehicle' : 'Container'} Details for IDF: {job.idf_no} BL: {job.bl_no}</h3>
                </div>
                <div className="dialog-body">
                    {/* 3. Conditionally render bulk assignment tools */}
                    {hasPermission('PORT REPORTS', 'can_edit') && !readOnly && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem', background: '#f4f4f4', padding: '10px', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{ width: 220, minWidth: 220 }}>
                                    <Select2Dropdown
                                        options={availableTransporters.map(t => ({ id: t.id, text: t.display_name }))}
                                        value={bulkTransporter}
                                        onChange={setBulkTransporter}
                                        valueKey="id"
                                        displayKey="text"
                                        placeholder="Bulk Assign Transporter"
                                        clearable
                                    />
                                </div>
                                <div style={{ width: 220, minWidth: 220 }}>
                                    <Select2Dropdown
                                        options={TANZANIA_REGIONS.map(r => ({ id: r, text: r }))}
                                        value={bulkRegion}
                                        onChange={setBulkRegion}
                                        valueKey="id"
                                        displayKey="text"
                                        placeholder="Bulk Assign Region"
                                        clearable
                                    />
                                </div>
                                <button className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={applyBulkChanges} disabled={(!bulkTransporter && !bulkRegion) || selectedContainerIds.length === 0}>Apply to Selected</button>
                            </div>
                            {!isVehicle && !isLCL && (
                                <div style={{ width: 800, minWidth: 800 }}>
                                    <label style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>RELEASED DATE</label>
                                    <input
                                        type="date"
                                        className="search-input"
                                        value={bulkReleaseDate}
                                        onChange={e => setBulkReleaseDate(e.target.value)}
                                        style={{ width: 120, minWidth: 120 }}
                                    />
                                    <button className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={applyBulkReleaseDate} disabled={!bulkReleaseDate || selectedContainerIds.length === 0}>Apply to Selected</button>
                                </div>
                            )}
                        </div>
                    )}
                    <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
                        <table className="jobs-table">
                            <thead>
                                <tr>
                                    <th><input type="checkbox" onChange={handleSelectAll} checked={selectedContainerIds.length === containers.length && containers.length > 0} /></th>
                                    {!isLCL && <th>{isVehicle ? 'CHASSIS NO' : 'CONTAINER NO'}</th>}
                                    {!isVehicle && !isLCL && <th>SIZE</th>}
                                    {!isVehicle && !isLCL && <th>TYPE</th>}
                                    {!isVehicle && !isLCL && <th>SEAL NO</th>}
                                    {!isVehicle && !isLCL && <th>KK SEAL NO</th>}
                                    {!isVehicle && !isLCL && <th style={{ width: '80px' }}>RELEASED DATE</th>}
                                    {!isVehicle && !isLCL && <th style={{ width: '80px' }}>FUTURE RELEASE DATE</th>}
                                    <th style={{ width: '220px' }}>TRANSPORTER</th>
                                    <th>REGION</th>
                                </tr>
                            </thead>
                            <tbody>
                                {containers.map(c => (
                                    <tr key={c.id}>
                                        {/* 4. Conditionally render checkbox for bulk actions */}
                                        <td>
                                            {hasPermission('PORT REPORTS', 'can_edit') && !readOnly && (
                                                <input type="checkbox" checked={selectedContainerIds.includes(c.id)} onChange={() => handleSelectionChange(c.id)} />
                                            )}
                                        </td>
                                        {!isLCL && <td>{c.container_no}</td>}
                                        {!isVehicle && !isLCL && <td>{c.size}</td>}
                                        {!isVehicle && !isLCL && <td>{c.container_type}</td>}
                                        {!isVehicle && !isLCL && <td>{c.seal_no}</td>}
                                        {/* 5. Make inputs read-only based on permission */}
                                        {!isVehicle && !isLCL && <td><input type="text" className="search-input" style={{width: '150px'}} value={c.kk_seal_no || ''} onChange={e => handleContainerChange(c.id, 'kk_seal_no', e.target.value)} readOnly={!hasPermission('PORT REPORTS', 'can_edit') || readOnly} /></td>}
                                        {!isVehicle && !isLCL && <td><input type="date" className="search-input" style={{width: '80px'}} value={c.release_date || ''} onChange={e => handleContainerChange(c.id, 'release_date', e.target.value)} readOnly={!hasPermission('PORT REPORTS', 'can_edit') || readOnly} /></td>}
                                        {!isVehicle && !isLCL && <td><input type="date" className="search-input" style={{width: '80px'}} value={fmtDateInput(c.future_release_date)} min={minFutureDateStr} onChange={e => handleContainerChange(c.id, 'future_release_date', e.target.value)} readOnly={!hasPermission('PORT REPORTS', 'can_edit') || readOnly} /></td>}
                                        <td>
                                            <div style={{ width: 220 }}>
                                                <Select2Dropdown
                                                    options={availableTransporters.map(t => ({ id: t.id, text: t.display_name }))}
                                                    value={c.transporter}
                                                    onChange={val => handleContainerChange(c.id, 'transporter', val)}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    placeholder="Select Transporter"
                                                    clearable
                                                    isDisabled={!hasPermission('PORT REPORTS', 'can_edit') || readOnly}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <Select2Dropdown
                                                options={TANZANIA_REGIONS.map(r => ({ id: r, text: r }))}
                                                value={c.region}
                                                onChange={val => handleContainerChange(c.id, 'region', val)}
                                                valueKey="id"
                                                displayKey="text"
                                                placeholder="Select Region"
                                                style={{ minWidth: 200 }}
                                                clearable
                                                isDisabled={!hasPermission('PORT REPORTS', 'can_edit') || readOnly}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="dialog-footer">
                    <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose}>Close</button>
                    {/* 6. Conditionally render Save button */}
                    {hasPermission('PORT REPORTS', 'can_edit') && !readOnly && (
                        <button type="button" className="dialog-btn dialog-btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Changes'}</button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- ICD Staff Dialog ---
const IcdStaffDialog = ({ icdName, staffList, currentStaffIds, onClose, onSaveSuccess, hasPermission }) => { // 7. Receive hasPermission
    const [selectedStaff, setSelectedStaff] = useState(currentStaffIds || []);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { icd_name: icdName, staff_ids: selectedStaff, exclude_released: true };
            const res = await fetch(`${API_BASE}/bulk-update-staff-by-icd/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to update staff.');
            const result = await res.json();
            toast.success(`Successfully updated staff for ${result.updated_job_count} jobs at ${icdName}.`);
            onSaveSuccess();
        } catch (e) {
            toast.error(e.message);
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="dialog-container" style={{ width: 500 }}>
                <div className="dialog-header">
                    <h3>Assign Staff for ICD: {icdName}</h3>
                </div>
                <div className="dialog-body">
                    <p style={{fontSize: '13px', color: '#666', marginTop: 0}}>This will assign the selected staff to all jobs in the Port Report for this ICD.</p>
                    <MultiSelectDropdown
                        id="icd-staff-select"
                        options={staffList.map(s => s.short_name || s.name)}
                        selectedValues={staffList.filter(s => selectedStaff.includes(s.id)).map(s => s.short_name || s.name)}
                        onChange={selectedDisplayNames => {
                            const ids = staffList.filter(s => selectedDisplayNames.includes(s.short_name || s.name)).map(s => s.id);
                            setSelectedStaff(ids);
                        }}
                        placeholder="Select Staff"
                        maxMenuHeight={220}
                    />
                </div>
                <div className="dialog-footer">
                    <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose}>Cancel</button>
                    {/* 8. Conditionally render Save button */}
                    {hasPermission('PORT REPORTS', 'can_edit') && (
                        <button type="button" className="dialog-btn dialog-btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save'}</button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Wharfage Report Tab ---
const WharfageReportTab = ({ hasPermission, onCountChange }) => { // 9. Receive hasPermission
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });

    const fetchWharfageJobs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/wharfage-report/`, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to fetch wharfage report data');
            const data = await res.json();
            setJobs(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only fetch if the user has permission
        if (hasPermission('WHARFAGE_REPORT', 'can_view')) {
            fetchWharfageJobs();
        }
    }, [fetchWharfageJobs, hasPermission]);

    useEffect(() => {
        if (onCountChange) onCountChange(jobs.length);
    }, [jobs, onCountChange]);


    // 10. Add permission check AFTER hooks
    if (!hasPermission('WHARFAGE_REPORT', 'can_view')) {
        return <div style={{padding: 20}}><h2>Permission Denied</h2><p>You do not have permission to view the Wharfage Report.</p></div>;
    }

    const openJobFormModal = async (jobId) => {
        try {
            const res = await fetch(`/api/documentation/jobs/${jobId}/`, { headers: getAuthHeaders() });
            if (!res.ok) {
                toast.error('Failed to load job details');
                return;
            }
            const data = await res.json();
            setJobFormModal({ isOpen: true, job: data });
        } catch (e) {
            toast.error('Error loading job');
        }
    };

    const closeJobFormModal = (refresh) => {
        setJobFormModal({ isOpen: false, job: null });
        if (refresh) {
            fetchWharfageJobs();
        }
    };

    const comparedJobs = jobs.filter(j => !j.wharfage_sent);
    const sentJobs = jobs.filter(j => j.wharfage_sent);

    const COMPARED_COLUMNS = [
        { key: 'sn', label: 'S/N' }, { key: 'job_id', label: 'JOB ID' },
        { key: 'idf_no', label: 'IDF NO.' }, { key: 'customer_name', label: 'CUSTOMER NAME' }, { key: 'bl_no', label: 'BL #' },
        { key: 'tansad_no', label: 'TANSAD NO.' }, { key: 'date', label: 'DATE' },
        { key: 'username', label: 'USERNAME' }, { key: 'sent', label: 'SENT' },
    ];

    const SENT_COLUMNS = [
        { key: 'sn', label: 'S/N' }, { key: 'job_id', label: 'JOB ID' }, 
        { key: 'idf_no', label: 'IDF NO.' }, { key: 'customer_name', label: 'CUSTOMER NAME' }, { key: 'bl_no', label: 'BL #' },
        { key: 'tansad_no', label: 'TANSAD NO.' }, 
        { key: 'wharfage_sent_at', label: 'DATE' }, // Corrected key
        { key: 'wharfage_sent_by_username', label: 'USERNAME' }, // Corrected key
    ];

    return (
        <div>
            {/* Compared Section */}
            <div style={{ marginBottom: 32 }}>
                <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8,background: '#f3dcb8ff', borderRadius: 6, padding: 8, fontSize: 16 }}>COMPARED</div>
                
                <TableScrollContainer>
                    <table className="jobs-table" style={{ overflow: 'visible' }}>
                        <thead>
                            <tr>{COMPARED_COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                        </thead>
                        <tbody>
                            {loading ? (<tr><td colSpan={COMPARED_COLUMNS.length}>Loading...</td></tr>)
                            : comparedJobs.length === 0 ? (<tr><td colSpan={COMPARED_COLUMNS.length}>NO RECORDS FOUND.</td></tr>)
                            : (
                                comparedJobs.map((job, idx) => {
                                    // Check if a "Wharfage Invoice" document exists for this job
                                    const hasWharfageInvoice = job.documents?.some(doc => doc.document_type.toLowerCase() === 'wharfage invoice');
                                    
                                    return (
                                        <tr 
                                            key={job.id}
                                            className={hasWharfageInvoice ? 'flash-yellow' : ''}
                                            title={hasWharfageInvoice ? 'Wharfage Invoice Saved but not Sent' : ''}
                                        >
                                            <td>{idx + 1}</td>
                                            <td>{toUpperDisplay(job.id)}</td>
                                            <td>{toUpperDisplay(job.idf_no)}</td>
                                            <td><strong>{toUpperDisplay(job.customer_name)}</strong></td>
                                            <td>{toUpperDisplay(job.bl_no)}</td>
                                            <td>{toUpperDisplay(job.tansad_no)}</td>
                                            <td>{new Date(job.compared_at).toLocaleDateString()}</td>
                                            <td>{toUpperDisplay(job.compared_by_username)}</td>
                                            {/* 11. Disable checkbox based on permission */}
                                            <td><input type="checkbox" onChange={() => openJobFormModal(job.id)} checked={false} disabled={!hasPermission('WHARFAGE_REPORT', 'can_edit')} /></td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        </table>
                </TableScrollContainer>
            </div>

            {/* Sent Section */}
            <div>
                <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#c2fcc2ff', borderRadius: 6, padding: 8, fontSize: 16 }}>SENT</div>
                <TableScrollContainer>
                    <table className="jobs-table" style={{ overflow: 'visible' }}>
                        <thead>
                            <tr>{SENT_COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                        </thead>
                        <tbody>
                            {loading ? (<tr><td colSpan={SENT_COLUMNS.length}>Loading...</td></tr>)
                            : sentJobs.length === 0 ? (<tr><td colSpan={SENT_COLUMNS.length}>NO RECORDS FOUND.</td></tr>)
                            : (
                                sentJobs.map((job, idx) => (
                                    <tr key={job.id}>
                                        <td>{idx + 1}</td>
                                        <td>{toUpperDisplay(job.id)}</td>
                                        <td>{toUpperDisplay(job.idf_no)}</td>
                                        <td><strong>{toUpperDisplay(job.customer_name)}</strong></td>
                                        <td>{toUpperDisplay(job.bl_no)}</td>
                                        <td>{toUpperDisplay(job.tansad_no)}</td>
                                        <td>{job.wharfage_sent_at ? new Date(job.wharfage_sent_at).toLocaleDateString() : 'N/A'}</td>
                                        <td>{toUpperDisplay(job.wharfage_sent_by_username) || 'N/A'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        </table>
                </TableScrollContainer>
            </div>

            {jobFormModal.isOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ 
                        width: '95%', 
                        maxWidth: '1400px', 
                        maxHeight: '90vh', // Limit height to 90% of the viewport
                        display: 'flex',      // Use flexbox for layout
                        flexDirection: 'column' // Stack children vertically
                    }}>
                        <div className="dialog-header" style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', // Pushes title and button apart
                            alignItems: 'center' 
                        }}>
                            <h3>Wharfage Document Upload & Email</h3>
                             <button className="icon-btn" onClick={() => closeJobFormModal(false)} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body" style={{ 
                            padding: 0, 
                            overflowY: 'auto', // Enable vertical scrollbar
                            flex: 1               // Allow body to fill available space
                        }}>
                            <JobForm
                                mode="view"
                                initialData={jobFormModal.job}
                                onClose={closeJobFormModal}
                                documentsEditable={true}
                                openDocumentsOnly={true}
                                wharfageOnly={true}
                                showEmailButtonOnly={true}
                                defaultEmailTemplateName="Wharfage Invoice"
                                documentTypeDepartment="Port Operations"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function formatDailyDate(dateStr, createdAt) {
  const pad = n => n.toString().padStart(2, '0');
  let datePart = dateStr || '';
  if (datePart) {
    const d = new Date(datePart);
    if (!isNaN(d)) datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  let timePart = '';
  if (createdAt) {
    const t = new Date(createdAt);
    if (!isNaN(t)) timePart = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
  }
  return { datePart, timePart };
}

const DailyPortReportsTab = ({ hasPermission, onCountChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [singleDate, setSingleDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const handleViewPdf = useCallback(async (row) => {
    try {
      const res = await fetch(row.view_pdf_url, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        throw new Error('Failed to load PDF preview.');
      }

      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        throw new Error('The generated PDF file is empty.');
      }

      const blobUrl = URL.createObjectURL(blob);
      const previewWindow = openFilePreview(blobUrl, `Daily_Port_Report_${row.date}.pdf`);

      // Revoke object URL when preview closes to avoid memory leaks.
      if (previewWindow && previewWindow.addEventListener) {
        previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not open PDF preview.');
    }
  }, []);

  const handleDownload = useCallback(async (url, filename) => {
    try {
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('Downloaded file is empty.');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e.message || 'Download failed.');
    }
  }, []);

  const fetchDailyReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (singleDate) {
        params.append('date', singleDate);
      } else {
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
      }
      const res = await fetch(`${API_BASE}/daily-port-reports/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch daily port reports.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [singleDate, fromDate, toDate]);

  useEffect(() => {
    fetchDailyReports();
  }, [fetchDailyReports]);

  useEffect(() => {
    if (onCountChange) onCountChange(rows.length);
  }, [rows, onCountChange]);

  if (!hasPermission('PORT REPORTS', 'can_view')) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Daily Port Reports.</p></div>;
  }

  return (
    <div>
      <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Single Date</label>
          <input type="date" className="search-input" value={singleDate} onChange={e => setSingleDate(e.target.value)} title="Single Date" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Date Range</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" className="search-input" value={fromDate} onChange={e => setFromDate(e.target.value)} title="From Date" />
            <span style={{ color: '#666', fontWeight: 700 }}>To</span>
            <input type="date" className="search-input" value={toDate} onChange={e => setToDate(e.target.value)} title="To Date" />
          </div>
        </div>
        <button className="add-btn" onClick={fetchDailyReports}>Apply Date Filter</button>
        <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => { setSingleDate(''); setFromDate(''); setToDate(''); }}>Clear Date Filter</button>
      </div>

      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th>DATE</th>
              <th>GENERATED BY</th>
              <th>VIEW PDF</th>
              <th>DOWNLOAD PDF</th>
              <th>DOWNLOAD EXCEL</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={5} className="no-data">No daily reports found.</td></tr>)}
            {!loading && rows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => { const { datePart, timePart } = formatDailyDate(r.date, r.created_at); return (<span>{datePart}{timePart && <><br /><span style={{ fontSize: 11, color: '#555' }}>{timePart}</span></>}</span>); })()}
                </td>
                <td>{r.generated_by || '-'}</td>
                <td>
                  <button
                    type="button"
                    className="icon-btn"
                    title="View PDF"
                    onClick={() => handleViewPdf(r)}
                  >
                    <i className="fa fa-eye" />
                  </button>
                </td>
                <td>
                  <button type="button" className="icon-btn" title="Download PDF" onClick={() => handleDownload(r.download_pdf_url, `Daily_Port_Report_${r.date}.pdf`)}>
                    <i className="fa fa-download" />
                  </button>
                </td>
                <td>
                  <button type="button" className="icon-btn" title="Download Excel" onClick={() => handleDownload(r.download_excel_url, `Daily_Port_Report_${r.date}.xlsx`)}>
                    <i className="fa fa-download" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    </div>
  );
};


// --- Main Port Report Tab ---
const MainPortReportTab = ({ onRefresh, rows, setRows, loading, setLoading, hasPermission, fixedRemarkFilter, onOpenDailyReports, onCountChange }) => { // 12. Receive hasPermission
    // ...existing code...
      // Released date range filter state
      // Default to last 2 weeks for Released tab
      const getDefaultReleasedDates = () => {
        const today = new Date();
        const prior = new Date();
        prior.setDate(today.getDate() - 13); // 14 days including today
        return {
          from: prior.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0],
        };
      };
      const [releasedFromDate, setReleasedFromDate] = useState(fixedRemarkFilter === 'RELEASED' ? getDefaultReleasedDates().from : '');
      const [releasedToDate, setReleasedToDate] = useState(fixedRemarkFilter === 'RELEASED' ? getDefaultReleasedDates().to : '');
      // Only apply filter when user clicks Apply
      const [appliedReleasedFromDate, setAppliedReleasedFromDate] = useState(fixedRemarkFilter === 'RELEASED' ? getDefaultReleasedDates().from : '');
      const [appliedReleasedToDate, setAppliedReleasedToDate] = useState(fixedRemarkFilter === 'RELEASED' ? getDefaultReleasedDates().to : '');
    // Filters
    const [idfFilter, setIdfFilter] = useState('');
    const [search, setSearch] = useState('');
    const [icdFilter, setIcdFilter] = useState('');
    const [staffFilter, setStaffFilter] = useState('');
    const [remarkFilter, setRemarkFilter] = useState(fixedRemarkFilter || '');

    // Dropdown data
    const [dropdowns, setDropdowns] = useState({ staff: [], transporters: [], lcl_transporters: [], icds: [], port_remarks: [] });

    // Dialog states
    const [containerDialogJob, setContainerDialogJob] = useState(null);
    const [icdStaffDialog, setIcdStaffDialog] = useState({ open: false, icdName: '', staffIds: [] });
    const [showPortConfigModal, setShowPortConfigModal] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });

    // Per-cell edit states (only wharfage, remarks and scanner are now inline)
    const [editingWharfageId, setEditingWharfageId] = useState(null);
    const [editingRemarksId, setEditingRemarksId] = useState(null);
    const [editingScannerId, setEditingScannerId] = useState(null);

    // Temp values
    const [tempWharfage, setTempWharfage] = useState('');
    const [tempRemarks, setTempRemarks] = useState('');
    const [tempReleaseDate, setTempReleaseDate] = useState('');
    const [tempScanner, setTempScanner] = useState(false);
    const [savingScanner, setSavingScanner] = useState(false);
    const [wharfageEditOptions, setWharfageEditOptions] = useState(WHARFAGE_OPTIONS);

    // Saving flags
    const [savingWharfage, setSavingWharfage] = useState(false);
    const [savingRemarks, setSavingRemarks] = useState(false);
    const [generatingDaily, setGeneratingDaily] = useState(false);
    const [previewingDaily, setPreviewingDaily] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    // Saved-row highlight
    const [flashJobId, setFlashJobId] = useState(null);
    const [flashIcd, setFlashIcd] = useState(null);

    useEffect(() => {
      if (!flashJobId) return;
      const t = setTimeout(() => setFlashJobId(null), 5000);
      return () => clearTimeout(t);
    }, [flashJobId]);

    useEffect(() => {
      if (!flashIcd) return;
      const t = setTimeout(() => setFlashIcd(null), 5000);
      return () => clearTimeout(t);
    }, [flashIcd]);

    // Drag-and-drop state
    const [dragSrcId, setDragSrcId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);

    // State for Pagination (Released Report only)
    const [releasedCurrentPage, setReleasedCurrentPage] = useState(1);
    const [releasedItemsPerPage, setReleasedItemsPerPage] = useState(25);
    const [releasedTotalCount, setReleasedTotalCount] = useState(0);

    // Create a memoized list of remarks including RELEASED
    const remarkOptions = useMemo(() => {
      const existingRemarks = dropdowns.port_remarks.map(pr => pr.text);
      const allRemarks = [...existingRemarks];
      if (!allRemarks.includes('RELEASED')) {
          allRemarks.push('RELEASED');
      }
      return allRemarks.map(r => ({ id: r, text: r }));
    }, [dropdowns.port_remarks]);

    const isReleasedEligible = useCallback((row) => {
      const doStatuses = Array.isArray(row?.do_statuses) ? row.do_statuses : [];
      const hasDoReceived = doStatuses.some(
        status => (status || '').trim().toUpperCase().startsWith('DO RECEIVED')
      );
      const isWharfagePaid = (row?.wharfage_status || '').trim().toUpperCase() === 'PAID';
      const isDutyPaid = !!row?.duty_paid;
      const isScannerDone = !!row?.scanner_report_done;

      return hasDoReceived && isWharfagePaid && isDutyPaid && isScannerDone;
    }, []);

    const getUnapprovedPermits = useCallback((row) => (row?.permit_details || []).filter(
        permit => (permit.status || '').trim().toUpperCase() !== 'APPROVED'
    ), []);

    const allContainersHaveTransporterAndSeal = useCallback((row) => {
      const isVehicle = (row?.cargo_type || '').toUpperCase() === 'VEHICLE';
      const items = isVehicle ? (row?.vehicles || []) : (row?.containers || []);
      if (items.length === 0) return true; // No containers/vehicles to check
      const hasTransporters = items.every(c => (c.transporter || c.transporter_text));
      if (isVehicle) return hasTransporters; // Vehicles: only transporter required, no KK Seal
      return hasTransporters && items.every(c => c.kk_seal_no);
    }, []);

    const allContainersHaveReleaseDate = useCallback((row) => {
      const cargo = (row?.cargo_type || '').toUpperCase();
      if (cargo === 'VEHICLE' || cargo === 'LCL') return true;
      const containers = row?.containers || [];
      if (containers.length === 0) return true;
      return containers.every(c => c.release_date);
    }, []);

    const getRemarkEditOptions = useCallback((row) => {
      return remarkOptions.filter(option => option.id !== 'RELEASED' || isReleasedEligible(row));
    }, [remarkOptions, isReleasedEligible]);

    const getTextColorForBackground = useCallback((hexColor) => {
      if (!hexColor) return '#000000';
      const rgb = parseInt(hexColor.slice(1), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = (rgb >> 0) & 0xff;
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luma < 128 ? '#FFFFFF' : '#000000';
    }, []);

    // Fetch dropdown data
    const fetchDropdowns = useCallback(async () => {
      try {
        const res = await fetch(`${API_BASE}/port-dropdowns/`, { headers: getAuthHeaders(false) });
        if (res.ok) {
          const data = await res.json();
          const remarksRes = await fetch('/api/masters/port-remarks/', { headers: getAuthHeaders(false) });
          const remarksData = remarksRes.ok ? await remarksRes.json() : [];

          setDropdowns({
            staff: data.staff || [],
            transporters: data.transporters || [],
            lcl_transporters: data.lcl_transporters || [],
            icds: data.icds || [],
            port_remarks: (remarksData.results || remarksData) || []
          });
        }
      } catch (e) {
        console.error(e);
      }
    }, []);

    // Fetch non-released rows from the API
    const fetchNonReleasedRows = useCallback(async (nextRemarkFilter, isBackgroundRefresh = false) => {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      try {
        const params = new URLSearchParams();
        if (nextRemarkFilter) {
          params.append('remark_filter', nextRemarkFilter);
        }
        const res = await fetch(`${API_BASE}/port-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
        if (!res.ok) throw new Error('Failed to fetch port report');
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
        setReleasedTotalCount(0);
      } catch (e) {
        console.error(e);
        if (!isBackgroundRefresh) {
          toast.error(e.message);
        }
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    }, [setLoading, setRows]);

    // Fetch released rows with server-side date range, filters and pagination
    const fetchReleasedRows = useCallback(async (isBackgroundRefresh = false) => {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      try {
        const params = new URLSearchParams();
        params.append('remark_filter', 'RELEASED');
        if (appliedReleasedFromDate) params.append('from_date', appliedReleasedFromDate);
        if (appliedReleasedToDate) params.append('to_date', appliedReleasedToDate);
        if (releasedCurrentPage) params.append('page', releasedCurrentPage);
        if (releasedItemsPerPage) params.append('page_size', releasedItemsPerPage);
        if (idfFilter.trim()) params.append('idf_no', idfFilter.trim());
        if (icdFilter) params.append('icd', icdFilter);
        if (staffFilter) params.append('staff', staffFilter);
        if (search.trim()) params.append('search', search.trim());

        const res = await fetch(`${API_BASE}/port-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
        if (!res.ok) throw new Error('Failed to fetch port report');
        const data = await res.json();
        if (data && Array.isArray(data.results)) {
          setRows(data.results);
          setReleasedTotalCount(data.count || 0);
        } else {
          setRows([]);
          setReleasedTotalCount(0);
        }
      } catch (e) {
        console.error(e);
        if (!isBackgroundRefresh) {
          toast.error(e.message);
        }
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    }, [setLoading, setRows, appliedReleasedFromDate, appliedReleasedToDate, releasedCurrentPage, releasedItemsPerPage, idfFilter, icdFilter, staffFilter, search]);

    const fetchRows = useCallback(async (isBackgroundRefresh = false) => {
      if (fixedRemarkFilter === 'RELEASED') {
        await fetchReleasedRows(isBackgroundRefresh);
      } else {
        await fetchNonReleasedRows(remarkFilter, isBackgroundRefresh);
      }
    }, [fixedRemarkFilter, remarkFilter, fetchReleasedRows, fetchNonReleasedRows]);

    useEffect(() => {
      fetchDropdowns();
    }, [fetchDropdowns]);

    useEffect(() => {
      if (fixedRemarkFilter !== 'RELEASED') {
        fetchNonReleasedRows(remarkFilter);
      }
    }, [fixedRemarkFilter, remarkFilter, fetchNonReleasedRows]);

    useEffect(() => {
      if (fixedRemarkFilter === 'RELEASED') {
        fetchReleasedRows();
      }
    }, [fixedRemarkFilter, fetchReleasedRows]);


    const clearAllFilters = async () => {
      setIdfFilter('');
      setSearch('');
      setIcdFilter('');
      setStaffFilter('');
      setReleasedCurrentPage(1);
      setReleasedTotalCount(0);

      const nextRemark = fixedRemarkFilter || '';
      setRemarkFilter(nextRemark);
    };

    const openJobFormModal = async (jobId) => {
        try {
            const res = await fetch(`/api/documentation/jobs/${jobId}/`, { headers: getAuthHeaders(false) });
            if (!res.ok) {
                toast.error('Error loading job');
                return;
            }
            const data = await res.json();
            setJobFormModal({ isOpen: true, job: data });
        } catch (e) {
            toast.error('Error loading job');
        }
    };

    const closeJobFormModal = () => {
        setJobFormModal({ isOpen: false, job: null });
    };

    // --- Drag-and-drop handlers ---
    const handleDragStart = useCallback((e, jobId) => {
        setDragSrcId(jobId);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e, jobId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (jobId !== dragSrcId) setDragOverId(jobId);
    }, [dragSrcId]);

    const handleDragEnd = useCallback(() => {
        setDragSrcId(null);
        setDragOverId(null);
    }, []);

    const handleDrop = useCallback(async (e, targetId) => {
        e.preventDefault();
        if (!dragSrcId || dragSrcId === targetId) { handleDragEnd(); return; }

        const srcRow = rows.find(r => r.id === dragSrcId);
        const tgtRow = rows.find(r => r.id === targetId);
        if (!srcRow || !tgtRow) { handleDragEnd(); return; }
        if (srcRow.icd !== tgtRow.icd) {
            toast.warning('Rows can only be reordered within the same ICD.');
            handleDragEnd();
            return;
        }

        // Reorder rows array: remove src, insert before or after target depending on direction
        const newRows = [...rows];
        const srcIdx = newRows.findIndex(r => r.id === dragSrcId);
        const origTgtIdx = newRows.findIndex(r => r.id === targetId);
        const movingDown = srcIdx < origTgtIdx;
        newRows.splice(srcIdx, 1);
        const newTgtIdx = newRows.findIndex(r => r.id === targetId);
        // When moving down, insert after the target; when moving up, insert before it
        newRows.splice(movingDown ? newTgtIdx + 1 : newTgtIdx, 0, srcRow);
        setRows(newRows);
        handleDragEnd();

        // Persist: assign sequential sort_order for all jobs in this ICD
        const icdGroup = newRows.filter(r => r.icd === srcRow.icd);
        const orders = icdGroup.map((r, i) => ({ id: r.id, sort_order: i }));
        try {
            const res = await fetch(`${API_BASE}/update-port-sort-order/`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders }),
            });
            if (!res.ok) throw new Error('Failed to save order.');
        } catch (err) {
            toast.error('Failed to save row order.');
            fetchRows(); // revert on error
        }
    }, [dragSrcId, rows, setRows, handleDragEnd, fetchRows]);

    const handleDownloadDailyReportFile = async (fileType) => {
      const isExcel = fileType === 'excel';
      const setLoading = isExcel ? setDownloadingExcel : setDownloadingPdf;
      const ext = isExcel ? 'xlsx' : 'pdf';
      const filename = `Port_Report_${new Date().toISOString().split('T')[0]}.${ext}`;

      
        setLoading(true);
        try {
          const exportRows = rowsToShow || [];
          if (exportRows.length === 0) {
            toast.warning('No released rows to export.');
            return;
          }

          const headers = [
            'IDF NO',
            'VESSEL',
            'VOYAGE',
            'CLIENT REF',
            'LCL GW / DG',
            'CONTAINER SIZE',
            'ICD',
            'CUSTOMER NAME',
            'WHARFAGE',
            'BL TYPE',
            'DO STATUS',
            'MATERIAL',
            'STAFF',
            'TRANSPORTER',
            'SCN DONE',
            'PORT REMARKS',
            'RELEASED DATE',
          ];

          const data = exportRows.map(r => [
            toUpperDisplay(r.idf_no),
            toUpperDisplay(r.vessel),
            toUpperDisplay(r.voyage_number),
            toUpperDisplay(r.client_ref),
            formatNumber(r.lcl_gw_dg),
            toUpperDisplay(r.container_size),
            toUpperDisplay(r.icd),
            toUpperDisplay(r.customer_name),
            toUpperDisplay(r.wharfage_status),
            toUpperDisplay(r.bl_type),
            formatDoStatus(r.do_statuses, r.drop_off_expiry_date),
            toUpperDisplay(r.material),
            toUpperDisplay((r.port_staff_names || []).join(', ')),
            toUpperDisplay((r.port_transporters || []).map(t => t.display_text).filter(Boolean).join(', ')) || '-',
            r.scanner_report_done ? 'YES' : 'NO',
            getPortRemarksDisplay(r),
            r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '-',
          ]);

          if (isExcel) {
            const aoa = [headers, ...data];
            const colCount = headers.length;
            const ws = XLSX.utils.aoa_to_sheet(aoa);

            ws['!cols'] = headers.map((header, columnIndex) => {
              const values = data.map(row => String(row[columnIndex] ?? ''));
              const widestValue = Math.max(header.length, ...values.map(value => value.length), 12);
              return { wch: Math.min(widestValue + 2, 40) };
            });

            const borderAll = {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } }
            };

            const headerStyle = {
              font: { bold: true, color: { rgb: '000000' } },
              alignment: { horizontal: 'center' },
              border: borderAll
            };

            const dataStyle = { border: borderAll };

            for (let c = 0; c < colCount; c++) {
              const headerCell = ws[XLSX.utils.encode_cell({ r: 0, c })];
              if (headerCell) headerCell.s = headerStyle;
              for (let r = 0; r < data.length; r++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r + 1, c })];
                if (cell) cell.s = dataStyle;
              }
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Released Report');
            XLSX.writeFile(wb, `Port_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
          } else {
            const doc = new jsPDF('l', 'mm', 'a4');
            doc.setFontSize(10);
            doc.text('Port Report', 14, 10);
            autoTable(doc, {
              head: [headers],
              body: data,
              startY: 15,
              theme: 'grid',
              styles: { fontSize: 6, cellPadding: 1, overflow: 'linebreak' },
              headStyles: { fillColor: [220, 220, 220], textColor: 0, halign: 'center', fontStyle: 'bold' },
              margin: { top: 14, left: 10, right: 10, bottom: 10 },
              didDrawPage: () => { doc.setFontSize(10); doc.text('Released Report', 14, 10); },
            });
            doc.save(`Port_Report_${new Date().toISOString().split('T')[0]}.pdf`);
          }
        } catch (e) {
          console.error(e);
          toast.error(`Failed to export ${ext.toUpperCase()}.`);
        } finally {
          setLoading(false);
        }
        return;
      

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/download-daily-port-report/${fileType}/`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Failed to download ${fileType.toUpperCase()}.`);
        }
        const blob = await res.blob();
        if (!blob.size) throw new Error(`The ${fileType.toUpperCase()} file is empty.`);
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error(e);
        toast.error(e.message || `Failed to download ${fileType.toUpperCase()}.`);
      } finally {
        setLoading(false);
      }
    };

    const handlePreviewDailyReport = async () => {      setPreviewingDaily(true);
      try {
        const res = await fetch(`${API_BASE}/preview-daily-port-report/`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to generate preview.');
        }
        const blob = await res.blob();
        if (!blob.size) throw new Error('The preview PDF is empty.');
        const blobUrl = URL.createObjectURL(blob);
        openFilePreview(blobUrl, `Preview_Port_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      } catch (e) {
        console.error(e);
        toast.error(e.message || 'Failed to generate preview.');
      } finally {
        setPreviewingDaily(false);
      }
    };

    const executeGenerateDailyReport = async () => {
      setGeneratingDaily(true);
      try {
        // 1) Clear all filters and refresh base report rows before generating.
        setIdfFilter('');
        setSearch('');
        setIcdFilter('');
        setStaffFilter('');
        setRemarkFilter('');

        // 2) Trigger server-side PDF/Excel generation and persistence.
        const res = await fetch(`${API_BASE}/generate-daily-port-report/`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            report_date: new Date().toISOString().split('T')[0]
          })
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to generate daily report.');
        }

        toast.success('Daily Port Report generated and saved successfully.');
        if (onOpenDailyReports) {
          onOpenDailyReports();
        }
      } catch (e) {
        console.error(e);
        toast.error(e.message || 'Failed to generate daily report.');
      } finally {
        setGeneratingDaily(false);
      }
    };

    const handleGenerateDailyReport = () => {
      const missingRemarks = rows.filter(r => !(r.raw_port_remarks || r.port_remarks || '').trim());
      if (missingRemarks.length > 0) {
        toast.error(`Port Remarks are required before generating the report. ${missingRemarks.length} record(s) are missing remarks.`);
        return;
      }
      setConfirmDialog({
        isOpen: true,
        title: 'Generate Daily Report',
        message: 'Generating this report will overwrite any previously generated report for today. Are you sure you would like to generate this report now?',
        onConfirm: () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          executeGenerateDailyReport();
        },
        onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
      });
    };

    // Memoized filtered rows
    const filteredRows = useMemo(() => {
        let data = [...rows];
        // Released tab uses server-side filtering and pagination
        if (fixedRemarkFilter === 'RELEASED') {
            return data;
        }
        // Apply client-side filters
        if (idfFilter.trim()) {
          const n = idfFilter.trim().toUpperCase();
          data = data.filter(r => (r.idf_no || '').toUpperCase().includes(n));
        }
        if (icdFilter) {
          const selected = (dropdowns.icds || []).find(i => String(i.id) === String(icdFilter));
          const selectedShort = ((selected?.short_name || selected?.name) || '').toUpperCase();
          if (selectedShort) {
            const icdNameToShort = {};
            (dropdowns.icds || []).forEach(i => {
              const short = (i.short_name || i.name || '').toUpperCase();
              if (i.name) icdNameToShort[(i.name || '').toUpperCase()] = short;
              if (i.short_name) icdNameToShort[(i.short_name || '').toUpperCase()] = short;
              if (i.code) icdNameToShort[(i.code || '').toUpperCase()] = short;
            });
            data = data.filter(r => (icdNameToShort[(r.icd || '').toUpperCase()] || (r.icd || '').toUpperCase()) === selectedShort);
          }
        }
        if (staffFilter) {
          data = data.filter(r => (r.port_staff_ids || []).includes(parseInt(staffFilter, 10)));
        }
        if (search.trim()) {
          const needle = search.toUpperCase();
          data = data.filter(r =>
            [
              r.id, r.idf_no, r.kk_file_no, r.customer_name, r.vessel,
              r.client_ref, r.material, r.icd
            ].some(field => field && field.toString().toUpperCase().includes(needle))
          );
        }
        return data;
    }, [rows, idfFilter, icdFilter, staffFilter, search, dropdowns.icds]);

    // Compute max remark length for column width (after filteredRows is defined)
    const maxRemarkLength = useMemo(() => {
        return filteredRows.reduce((max, r) => Math.max(max, getPortRemarksDisplay(r).length), 0);
    }, [filteredRows]);
    // Set min width based on max remark length (approx. 8px per char, min 120, max 400)
    const remarksColWidth = Math.min(Math.max(maxRemarkLength * 8, 120), 400);


    // Patch helper
    const patchJob = async (id, payload, savingSetter) => {
      if (savingSetter) savingSetter(true);
      try {
        const res = await fetch(`${API_BASE}/${id}/`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          toast.success("Update successful!");
          return true;
        } else {
          throw new Error("Failed to save.");
        }
      } catch (e) {
        console.error(e);
        toast.error(e.message);
        return false;
      } finally {
        if (savingSetter) savingSetter(false);
      }
    };

    // Wharfage editing
    const startEditWharfage = row => {
      setEditingWharfageId(row.id);
      setTempWharfage(row.wharfage_status || '');
      // Set dropdown options based on 'compared' status
      let opts = row.compared ? WHARFAGE_OPTIONS : ['GATE OUT'];
      // DUTY NOT PAID unmarks duty paid without changing wharfage — delete-permission only
      if (hasPermission('PORT REPORTS', 'can_delete') && row.duty_paid) {
        opts = [...opts, 'DUTY NOT PAID'];
      }
      setWharfageEditOptions(opts);
    };
    const cancelWharfage = () => setEditingWharfageId(null);
    const saveWharfage = row => {
      if (tempWharfage === 'DUTY NOT PAID') {
        setSavingWharfage(true);
        fetch(`${API_BASE}/${row.id}/set-duty-paid/`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ duty_paid: false })
        }).then(async (res) => {
          if (!res.ok) throw new Error('Failed to update duty paid.');
          toast.success('Duty Paid removed.');
          await fetchRows(true);
          setFlashJobId(row.id);
          cancelWharfage();
        }).catch(e => toast.error(e.message || 'Failed to update duty paid.'))
          .finally(() => setSavingWharfage(false));
        return;
      }
      patchJob(row.id, { wharfage_status: tempWharfage || null }, setSavingWharfage).then(async (ok) => {
        if (ok) { await fetchRows(true); setFlashJobId(row.id); }
        cancelWharfage();
      });
    };

    // Remarks editing
    const startEditRemarks = row => {
      setEditingRemarksId(row.id);
      setTempRemarks(row.raw_port_remarks || ''); // Use the raw value for editing
    };
    const cancelRemarks = () => { setEditingRemarksId(null); setTempReleaseDate(''); };
    const saveRemarks = row => {
      const unapprovedPermits = getUnapprovedPermits(row);
      if (tempRemarks === 'RELEASED' && unapprovedPermits.length > 0) {
          const permitList = unapprovedPermits.map(permit => `${permit.name} (${permit.status})`).join(', ');
          toast.error(`You cannot release. The following Permits have not been approved: ${permitList}`);
          return;
      }

      if (tempRemarks === 'RELEASED' && !isReleasedEligible(row)) {
          toast.error('RELEASED can only be selected when DO status is DO RECEIVED, wharfage is PAID, duty is PAID, and scanner report is DONE.');
          return;
      }

      if (tempRemarks === 'RELEASED' && 
          row.cargo_type && 
          (row.cargo_type.toUpperCase() === 'FCL' || row.cargo_type.toUpperCase() === 'VEHICLE') && 
          !allContainersHaveTransporterAndSeal(row)) {
          const isVehicleRow = row.cargo_type.toUpperCase() === 'VEHICLE';
          toast.warning(isVehicleRow
              ? 'Ensure all vehicles have a Transporter assigned before releasing.'
              : 'Ensure all containers have Transporter and KK Seal before releasing.');
          setContainerDialogJob(row);
          return;
      }

      if (tempRemarks === 'RELEASED' && !tempReleaseDate) {
          toast.error('Please enter a Release Date before saving RELEASED.');
          return;
      }

      if (tempRemarks === 'RELEASED' &&
          row.cargo_type &&
          row.cargo_type.toUpperCase() === 'FCL' &&
          row.containers?.length > 1 &&
          !allContainersHaveReleaseDate(row)) {
          toast.warning('Ensure all containers have a Release Date before releasing.');
          setContainerDialogJob(row);
          return;
      }

      if (tempRemarks === 'RELEASED' && row.is_fund_request && !row.fund_request_released) {
          toast.error('Cannot Release. Awaiting Funds from Client');
          return;
      }

      const unReleasing = fixedRemarkFilter === 'RELEASED' && tempRemarks !== 'RELEASED';
      const saveAction = () => {
          const payload = { port_remarks: tempRemarks };
          if (tempRemarks === 'RELEASED' && tempReleaseDate) payload.release_date = tempReleaseDate;
          patchJob(row.id, payload, setSavingRemarks).then(async (ok) => {
              if (!ok) return;
              if (unReleasing) {
                  toast.warning('Please ensure to update the container release date');
              }
              if (tempRemarks === 'RELEASED' && tempReleaseDate && row.cargo_type?.toUpperCase() === 'FCL' && row.containers?.length === 1) {
                  try {
                      await fetch(`${API_BASE}/bulk-update-containers/`, {
                          method: 'POST',
                          headers: getAuthHeaders(),
                          body: JSON.stringify([{ id: row.containers[0].id, release_date: tempReleaseDate }])
                      });
                  } catch (e) {
                      console.error('Failed to update container release_date:', e);
                  }
              }
              await fetchRows(true);
              setFlashJobId(row.id);
              cancelRemarks();
          });
      };

      if (!fixedRemarkFilter && tempRemarks === 'RELEASED') {
          setConfirmDialog({
              isOpen: true,
              title: 'Confirm Release',
              message: 'Are you sure you want to mark this job as RELEASED? It will be hidden from the default report view.',
              onConfirm: () => {
                  setConfirmDialog({ ...confirmDialog, isOpen: false });
                  saveAction();
              },
              onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false })
          });
      } else if (fixedRemarkFilter === 'RELEASED' && tempRemarks !== 'RELEASED') {
          setConfirmDialog({
              isOpen: true,
              title: 'Confirm Un-Release',
              message: (
                  <>
                      Are you sure you want to change this job from RELEASED? It will be moved back to the main Port Report.
                      <div style={{ color: 'red', fontWeight: 'bold' }}>Please ensure to update the container release date</div>
                  </>
              ),
              onConfirm: () => {
                  setConfirmDialog({ ...confirmDialog, isOpen: false });
                  saveAction();
              },
              onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false })
          });
      } else {
          saveAction();
      }
    };

    const startEditScanner = row => {
      setEditingScannerId(row.id);
      setTempScanner(!!row.scanner_report_done);
    };
    const cancelScanner = () => { setEditingScannerId(null); setTempScanner(false); };
    const saveScanner = (row, value) => {
      patchJob(row.id, { scanner_report_done: value }, setSavingScanner).then(async (ok) => {
        if (ok) { await fetchRows(true); setFlashJobId(row.id); }
        setEditingScannerId(null);
      });
    };
    const handleScannerChange = (row, value) => {
      if (value) {
        setConfirmDialog({
          isOpen: true,
          title: 'Confirm Scanner Report',
          message: 'Are you sure the scanner report is done for this job?',
          onConfirm: () => {
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            setTempScanner(true);
            saveScanner(row, true);
          },
          onCancel: () => {
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            setTempScanner(false);
          }
        });
      } else {
        setTempScanner(false);
        saveScanner(row, false);
      }
    };

    const handleClearAllStaff = () => {
      setConfirmDialog({
        isOpen: true,
        title: 'Clear All Staff',
        message: 'This will remove all staff assignments from every job in the port report. Are you sure?',
        onConfirm: async () => {
          setConfirmDialog(d => ({ ...d, isOpen: false }));
          const uniqueIcds = [...new Set(rows.map(r => r.icd).filter(Boolean))];
          try {
            await Promise.all(uniqueIcds.map(icdName =>
              fetch(`${API_BASE}/bulk-update-staff-by-icd/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ icd_name: icdName, staff_ids: [], exclude_released: true })
              })
            ));
            toast.success('All staff cleared successfully.');
            await fetchRows(true);
          } catch (e) {
            toast.error('Failed to clear staff.');
          }
        },
        onCancel: () => setConfirmDialog(d => ({ ...d, isOpen: false })),
      });
    };

    const handleOpenIcdStaffDialog = (icdName) => {
      // Find the first job with this ICD to get current staff as a starting point
      const jobWithIcd = rows.find(r => r.icd === icdName);
      setIcdStaffDialog({
          open: true,
          icdName: icdName,
          staffIds: jobWithIcd ? jobWithIcd.port_staff_ids : []
      });
    };

    const renderContainerSizeList = (sizesString) => {
      if (!sizesString || typeof sizesString !== 'string') {
          return '-';
      }
      const parts = sizesString.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length === 0) {
          return '-';
      }
      // If it's a single item that doesn't look like a container summary (e.g., LCL CBM), just display it as text.
      if (parts.length === 1 && !parts[0].toLowerCase().includes('x')) {
          return parts[0];
      }
      return (
          <ul style={{ margin: 0, paddingLeft: '1.2em', whiteSpace: 'nowrap' }}>
              {parts.map((size, index) => (
                  <li key={index}>{size}</li>
              ))}
          </ul>
      );
    };

    // Wharfage badge
    const wharfageBadge = (wharfageStatus, dutyIsPaid, dutyPaidDate) => {
      const wharfageText = (wharfageStatus || '').toUpperCase();
      let backgroundColor = !wharfageText?'transparent':'#f5c6c6'; // Default: Light red (Duty not paid)
      let textColor = '#000';

      if (wharfageText === 'GATE OUT') {
          backgroundColor = '#ffe0a0';
      } else if (wharfageText === 'PAID' && dutyIsPaid) {
          backgroundColor = '#b3d1ff';
       } else if (wharfageText === 'NOT PAID' && dutyIsPaid) {
          backgroundColor = '#d9d9d9'; // Light grey (Duty paid, but wharfage not paid/gate out)
      }
     
      const dutyTitle = dutyIsPaid
        ? `Duty Paid${dutyPaidDate ? ` (${new Date(dutyPaidDate).toLocaleDateString('en-GB')})` : ''}`
        : 'Duty Not Paid';

      return (
        <span
          title={dutyTitle}
          style={{
            background: backgroundColor,
            color: textColor,
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: 11,
            display: 'inline-block',
            minWidth: 70,
            textAlign: 'center'
          }}
        >
          {wharfageText}
        </span>
      );
    };

    const getDropOffExpiryFlashProps = (dropOffExpiryDateStr, doExpiryDateStr, containers = []) => {
      let titleParts = [];
      let className = '';
      let doExpired = false;
      let doToday = false;
      let dropExpired = false;
      let dropToday = false;

      // Format DO Expiry Date if it exists
      if (doExpiryDateStr) {          
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalize to start of day

          const doExpiryDate = new Date(doExpiryDateStr);
          doExpiryDate.setHours(0, 0, 0, 0); // Normalize to start of day

          const timeDiffDO = today.getTime() - doExpiryDate.getTime();
          const dayDiffDO = Math.ceil(timeDiffDO / (1000 * 3600 * 24));

          const formattedDODate = doExpiryDate.toLocaleDateString('en-GB'); // DD/MM/YYYY

          if (dayDiffDO > 0) {
              doExpired = true;
              titleParts.push(`DO expired by ${dayDiffDO} day(s) since ${formattedDODate}`);
          } else if (dayDiffDO === 0) {
              doToday = true;
              titleParts.push(`DO expires today, ${formattedDODate}`);
          } else {
              // If not expired, just show the date if it exists
              titleParts.push(`DO Expiry: ${formattedDODate}`);
          }
      }

      if (dropOffExpiryDateStr) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day

        const dropOffExpiryDate = new Date(dropOffExpiryDateStr);
        dropOffExpiryDate.setHours(0, 0, 0, 0); // Normalize to start of day

        const timeDiffDropOff = today.getTime() - dropOffExpiryDate.getTime();
        const dayDiffDropOff = Math.ceil(timeDiffDropOff / (1000 * 3600 * 24));

        const formattedDropOffDate = dropOffExpiryDate.toLocaleDateString('en-GB'); // DD/MM/YYYY

        if (dayDiffDropOff > 0) {
            dropExpired = true;
            titleParts.push(`Drop-off expired by ${dayDiffDropOff} day(s) since ${formattedDropOffDate}`);
        } else if (dayDiffDropOff === 0) {
            dropToday = true;
            titleParts.push(`Drop-off expires today, ${formattedDropOffDate}`);
        } else {
            // If not expired, just show the date if it exists
            titleParts.push(`Drop-off Expiry: ${formattedDropOffDate}`);
        }
      }

      if (doExpired && dropExpired) {
          className = 'flash-yellow-orange';
      } else if (doExpired || dropExpired) {
          className = 'flash-red';
      } else if (doToday || dropToday) {
          className = 'flash-orange';
      }

      let title = titleParts.join(' | ');

      // Per-container drop-off expiry summary
      const dateCounts = {};
      for (const c of (containers || [])) {
        if (c.drop_off_expiry_date) {
          const d = new Date(c.drop_off_expiry_date).toLocaleDateString('en-GB');
          dateCounts[d] = (dateCounts[d] || 0) + 1;
        }
      }
      const dateEntries = Object.entries(dateCounts);
      if (dateEntries.length > 0) {
        const toIso = s => { const [dd, mm, yyyy] = s.split('/'); return `${yyyy}-${mm}-${dd}`; };
        const summary = dateEntries
          .sort(([a], [b]) => toIso(a).localeCompare(toIso(b)))
          .map(([date, count]) => `${count} container${count > 1 ? 's' : ''} - ${date}`)
          .join('\n');
        title += `\n\nDrop-off Expiry by Container:\n${summary}`;
      }

      return { className, title };
    };

    // Initialize sortable hook for filtered rows
    const { items: sortedRows, requestSort, getSortIndicator } = useSortableTable(filteredRows);


    // --- Released Date Range Filter State ---
    // ...existing code...
    // ...existing code...

    // --- Show all rows, filtered and sorted by release_date desc (nulls last) for Released tab ---
    const filteredRowsWithReleasedDate = useMemo(() => {
      let data = [...sortedRows];
      if (fixedRemarkFilter === 'RELEASED') {
        // Filter by applied released date range
        if (appliedReleasedFromDate && appliedReleasedToDate) {
          const from = new Date(appliedReleasedFromDate);
          const to = new Date(appliedReleasedToDate);
          data = data.filter(r => {
            if (!r.release_date) return false;
            const d = new Date(r.release_date);
            return d >= from && d <= to;
          });
        }
        // Sort by release_date desc (nulls last)
        data.sort((a, b) => {
          if (!a.release_date && !b.release_date) return 0;
          if (!a.release_date) return 1;
          if (!b.release_date) return -1;
          return new Date(b.release_date) - new Date(a.release_date);
        });
      }
      return data;
    }, [sortedRows, fixedRemarkFilter, appliedReleasedFromDate, appliedReleasedToDate]);

    // --- Use correct rows for table rendering ---
    const rowsToShow = fixedRemarkFilter === 'RELEASED' ? filteredRowsWithReleasedDate : sortedRows;

    // Report the filtered count to the parent tab bar
    useEffect(() => {
        if (onCountChange) {
            const count = fixedRemarkFilter === 'RELEASED' ? releasedTotalCount : filteredRows.length;
            onCountChange(count);
        }
    }, [filteredRows, releasedTotalCount, fixedRemarkFilter, onCountChange]);

    // Pagination logic for released report
    const releasedTotalPages = useMemo(() => {
      if (fixedRemarkFilter !== 'RELEASED') return 0;
      return Math.max(1, Math.ceil(releasedTotalCount / releasedItemsPerPage));
    }, [fixedRemarkFilter, releasedTotalCount, releasedItemsPerPage]);

    const releasedCurrentPageClamped = useMemo(() => {
      if (fixedRemarkFilter !== 'RELEASED') return 1;
      return Math.min(releasedCurrentPage, releasedTotalPages);
    }, [fixedRemarkFilter, releasedCurrentPage, releasedTotalPages]);

    const releasedStart = useMemo(() => {
      if (fixedRemarkFilter !== 'RELEASED') return 0;
      return (releasedCurrentPageClamped - 1) * releasedItemsPerPage;
    }, [fixedRemarkFilter, releasedCurrentPageClamped, releasedItemsPerPage]);

    const paginatedRowsToShow = useMemo(() => {
      if (fixedRemarkFilter !== 'RELEASED') return rowsToShow;
      // The backend already returns the correct page; no client-side slicing
      return filteredRowsWithReleasedDate;
    }, [fixedRemarkFilter, filteredRowsWithReleasedDate, rowsToShow]);

    const handleReleasedPageChange = (page) => {
      setReleasedCurrentPage(page);
    };

    const handleReleasedItemsPerPageChange = (size) => {
      setReleasedItemsPerPage(size);
      setReleasedCurrentPage(1); // Reset to first page when changing items per page
    };

    return (
      <>

        {/* Filters / toolbar */}
        <div className="toolbar" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            {/* Released Date Filter left, other filters right */}
            {fixedRemarkFilter === 'RELEASED' ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Released Date</label>
                <input
                  type="date"
                  className="search-input"
                  value={releasedFromDate}
                  onChange={e => setReleasedFromDate(e.target.value)}
                  style={{ minWidth: 130 }}
                  title="From Released Date"
                />
                <span style={{ color: '#666', fontWeight: 700 }}>To</span>
                <input
                  type="date"
                  className="search-input"
                  value={releasedToDate}
                  onChange={e => setReleasedToDate(e.target.value)}
                  style={{ minWidth: 130 }}
                  title="To Released Date"
                />
                <button
                  className="add-btn"
                  style={{ background: '#007bff', padding: '4px 14px' }}
                  onClick={() => {
                    setReleasedCurrentPage(1);
                    setAppliedReleasedFromDate(releasedFromDate);
                    setAppliedReleasedToDate(releasedToDate);
                  }}
                >Apply</button>
              </div>
            ) : <div />}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', alignItems: 'center', marginLeft: 'auto' }}>
              
              <Select2Dropdown
                options={[{ id: '', text: 'All ICDs' }, ...dropdowns.icds.map(i => ({ id: i.id, text: i.short_name || i.name }))]}
                value={icdFilter} onChange={val => setIcdFilter(val)} valueKey="id" displayKey="text"
                style={{ minWidth: 150 }} placeholder="ICD Filter"
              />
              <Select2Dropdown
                options={[{ id: '', text: 'All Staff' }, ...dropdowns.staff.map(s => ({ id: s.id, text: s.name }))]}
                value={staffFilter} onChange={val => setStaffFilter(val)} valueKey="id" displayKey="text"
                style={{ minWidth: 150 }} placeholder="Staff Filter"
              />
              {!fixedRemarkFilter && (
                <Select2Dropdown
                  options={[{ id: '', text: 'All Remarks' }, ...remarkOptions]}
                  value={remarkFilter} onChange={val => setRemarkFilter(val)} valueKey="id" displayKey="text"
                  style={{ minWidth: 160 }} placeholder="Remarks Filter"
                />
              )}
              <input className="search-input" placeholder="General Search" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 200 }} />
              <button className="add-btn" style={{ background: '#6c757d', whiteSpace: 'nowrap' }} onClick={clearAllFilters}>Clear All Filters</button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 10 }}>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {hasPermission('PORT REPORTS', 'can_edit') && !fixedRemarkFilter && (
                <>
                  <button className="add-btn" style={{ background: '#c0392b' }} onClick={handleClearAllStaff}>
                    Clear Staff
                  </button>
                  <button className="add-btn" style={{ background: '#e67e22' }} onClick={handlePreviewDailyReport} disabled={previewingDaily || generatingDaily}>
                    {previewingDaily ? 'Loading Preview...' : 'Preview Daily Report'}
                  </button>
                  <button className="add-btn" style={{ background: '#3f6b32' }} onClick={handleGenerateDailyReport} disabled={generatingDaily || previewingDaily}>
                    {generatingDaily ? 'Generating...' : 'Generate Daily Report'}
                  </button>
                </>
              )}
              {hasPermission('PORT REPORTS', 'can_edit') && (
                <>
                  <button className="add-btn" style={{ background: '#17a2b8' }} onClick={() => handleDownloadDailyReportFile('pdf')} disabled={downloadingPdf}>
                    {downloadingPdf ? 'Downloading...' : 'Download PDF'}
                  </button>
                  <button className="add-btn" style={{ background: '#007bff' }} onClick={() => handleDownloadDailyReportFile('excel')} disabled={downloadingExcel}>
                    {downloadingExcel ? 'Downloading...' : 'Download Excel'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
  
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 'bold',
            background: '#f2f6ff',
            padding: '8px 12px',
            borderRadius: 6,
            marginBottom: 8
        }}>
          <span>{fixedRemarkFilter === 'RELEASED' ? 'RELEASED REPORT' : 'PORT REPORT'}</span>
          {fixedRemarkFilter === 'RELEASED' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ color: '#555' }}>Rows per page:</span>
              {[25, 50, 100].map(size => (
                <button key={size} onClick={() => handleReleasedItemsPerPageChange(size)}
                  style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: 'pointer', fontWeight: releasedItemsPerPage === size ? 'bold' : 'normal', background: releasedItemsPerPage === size ? '#007bff' : '#fff', color: releasedItemsPerPage === size ? '#fff' : '#333' }}>
                  {size}
                </button>
              ))}
            </div>
          )}
          {/* 13. Conditionally render config button */}
          {hasPermission('PORT CONFIGURATIONS', 'can_view') && !fixedRemarkFilter && (
            <button 
                className="icon-btn" 
                title="Port Configurations"
                onClick={() => setShowPortConfigModal(true)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }}
            >
                <i className="fa-solid fa-cogs" />
            </button>
          )}
        </div>

        <TableScrollContainer>
          <style>{'@keyframes port-danger-flash { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } } .port-save-flash, .port-save-flash td { background-color: #fff3cd !important; }'}</style>
          <table className="jobs-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th onClick={() => requestSort('idf_no')} style={{ position: 'sticky', left: 0, zIndex: 20, backgroundColor: '#f8f9fa' }}>IDF NO {getSortIndicator('idf_no')}</th>
                <th style={{ whiteSpace: 'pre-line' }} onClick={() => requestSort('vessel')}>
                  <span style={{ fontWeight: 'bold' }}>VESSEL</span> {getSortIndicator('vessel')}<br />
                  <span style={{ fontWeight: 'normal' }}>VOYAGE</span>
                </th>
                <th onClick={() => requestSort('client_ref')}>CLIENT REF {getSortIndicator('client_ref')}</th>
                <th onClick={() => requestSort('lcl_gw_dg')} style={{ whiteSpace: 'pre-line' }}>LCL GW / DG {getSortIndicator('lcl_gw_dg')}</th>
                <th onClick={() => requestSort('container_size')}>CONTAINER SIZE {getSortIndicator('container_size')}</th>
                <th onClick={() => requestSort('icd')}>ICD {getSortIndicator('icd')}</th>
                <th onClick={() => requestSort('customer_name')}>CUSTOMER NAME {getSortIndicator('customer_name')}</th>
                <th onClick={() => requestSort('wharfage_status')}>WHARFAGE {getSortIndicator('wharfage_status')}</th>
                <th onClick={() => requestSort('bl_type')}>BL TYPE {getSortIndicator('bl_type')}</th>
                <th onClick={() => requestSort('do_statuses')}>DO STATUS {getSortIndicator('do_statuses')}</th>
                <th onClick={() => requestSort('material')} style={{ minWidth: 90, whiteSpace: 'normal', wordBreak: 'break-word' }}>MATERIAL {getSortIndicator('material')}</th>
                <th onClick={() => requestSort('port_staff_names')}>STAFF {getSortIndicator('port_staff_names')}</th>
                <th onClick={() => requestSort('port_transporters')}>TRANSPORTER {getSortIndicator('port_transporters')}</th>
                <th onClick={() => requestSort('scanner_report_done')}>SCN DONE {getSortIndicator('scanner_report_done')}</th>
                <th onClick={() => requestSort('port_remarks')} style={{ minWidth: remarksColWidth, width: remarksColWidth }}>PORT REMARKS {getSortIndicator('port_remarks')}</th>
                {fixedRemarkFilter === 'RELEASED' && (
                  <th onClick={() => requestSort('release_date')} style={{ whiteSpace: 'nowrap' }}>RELEASED DATE {getSortIndicator('release_date')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={18} style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                      <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                      <div style={{ color: '#666', fontSize: '14px' }}>Loading port report data...</div>
                      <div style={{ color: '#999', fontSize: '12px' }}>This may take a moment for large datasets</div>
                    </div>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </td>
                </tr>
              )}
              {!loading && (fixedRemarkFilter === 'RELEASED' ? paginatedRowsToShow.length === 0 : rowsToShow.length === 0) && (<tr><td colSpan={18} className="no-data">No records found.</td></tr>)}
              {!loading && (() => {
                let lastIcd = null;
                const rowsWithSeparators = [];
                const currentRowsToShow = fixedRemarkFilter === 'RELEASED' ? paginatedRowsToShow : rowsToShow;
                currentRowsToShow.forEach((r, idx) => {
                  const flashProps = fixedRemarkFilter === 'RELEASED'
                    ? { className: '', title: '' }
                    : getDropOffExpiryFlashProps(r.drop_off_expiry_date, r.do_expiry_date, r.containers);
                  const missingPortRemarks = !(r.raw_port_remarks || r.port_remarks || '').trim();
                  const isLastRow = idx === currentRowsToShow.length - 1;
                  const icdColor = r.icd_color || '#FFFFFF';
                  const icdTextColor = getTextColorForBackground(icdColor);
                  // Insert empty row if ICD changes (except before first row)
                  if (lastIcd !== null && r.icd !== lastIcd) {
                    rowsWithSeparators.push(
                      <tr key={`sep-${r.icd}-${idx}`} style={{ height: 10 }}>
                        <td colSpan={18} style={{ background: 'transparent', border: 'none', height: 10 }}></td>
                      </tr>
                    );
                  }
                  lastIcd = r.icd;
                  rowsWithSeparators.push(
                    <tr
                      key={r.id}
                      className={[flashProps.className, (flashJobId === r.id || flashIcd === r.icd) ? 'port-save-flash' : ''].filter(Boolean).join(' ')}
                      title={flashProps.title}
                      draggable={hasPermission('PORT REPORTS', 'can_edit') && !fixedRemarkFilter}
                      onDragStart={hasPermission('PORT REPORTS', 'can_edit') && !fixedRemarkFilter ? (e) => handleDragStart(e, r.id) : undefined}
                      onDragOver={(e) => handleDragOver(e, r.id)}
                      onDrop={(e) => handleDrop(e, r.id)}
                      onDragEnd={handleDragEnd}
                      style={{
                        cursor: hasPermission('PORT REPORTS', 'can_edit') ? 'grab' : undefined,
                        ...(dragOverId === r.id && dragSrcId !== r.id
                          ? { outline: '2px dashed #3f6b32', backgroundColor: '#e8f5e9' }
                          : {}),
                        ...(dragSrcId === r.id ? { opacity: 0.5 } : {}),
                      }}
                    >
                      <td title={`Job ID: ${r.id}, KK File: ${r.kk_file_no || 'N/A'}`} style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#fff', userSelect: 'text' }}>
                        <CopyCell text={r.idf_no}>
                          <button
                            type="button"
                            onClick={() => openJobFormModal(r.id)}
                            style={{
                              border: 'none',
                              padding: 0,
                              background: 'transparent',
                              color: '#0056b3',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              font: 'inherit'
                            }}
                          >
                            {toUpperDisplay(r.idf_no)}
                          </button>
                        </CopyCell>
                      </td>
                      <td style={{ whiteSpace: 'pre-line', userSelect: 'text' }}>
                        <CopyCell text={`${r.vessel || ''} ${r.voyage_number || ''}`.trim()}>
                          <span><span style={{ fontWeight: 'bold' }}>{toUpperDisplay(r.vessel)}</span><br />
                          <span style={{ fontWeight: 'normal' }}>{toUpperDisplay(r.voyage_number)}</span></span>
                        </CopyCell>
                      </td>
                      <td style={{ userSelect: 'text' }}><CopyCell text={r.client_ref}>{toUpperDisplay(r.client_ref)}</CopyCell></td>
                      <td style={{ userSelect: 'text' }}><CopyCell text={r.lcl_gw_dg}>{formatNumber(r.lcl_gw_dg)}</CopyCell></td>
                      <td title={`Total ${r.cargo_type && r.cargo_type.toUpperCase() === 'VEHICLE' ? 'V' : 'FCL'}: ${r.total_fcl}\nReleased: ${r.released_fcl}\nBalance: ${r.balance_fcl}`} style={{ userSelect: 'text' }}>
                        <CopyCell text={r.container_size}>
                          {hasPermission('PORT REPORTS', 'can_edit') ? (
                            <a href="#" onClick={(e) => { e.preventDefault(); setContainerDialogJob(r); }}>
                                {renderContainerSizeList(r.container_size)}
                            </a>
                          ) : (
                            renderContainerSizeList(r.container_size)
                          )}
                        </CopyCell>
                      </td>
                      <td style={{ backgroundColor: icdColor, color: icdTextColor, userSelect: 'text' }}>
                        <CopyCell text={r.icd}>
                          {hasPermission('PORT REPORTS', 'can_edit') && !fixedRemarkFilter ? (
                            <a href="#" onClick={(e) => { e.preventDefault(); handleOpenIcdStaffDialog(r.icd); }} style={{ color: 'inherit', fontWeight: 'bold' }}>
                                {toUpperDisplay(r.icd)}
                            </a>
                          ) : (
                            <span style={{fontWeight: 'bold'}}>{toUpperDisplay(r.icd)}</span>
                          )}
                        </CopyCell>
                      </td>
                      <td title={r.permit_status ? `Permit Status: ${r.permit_status}` : ''} style={{ userSelect: 'text' }}><CopyCell text={r.customer_name}><span><strong>{toUpperDisplay(r.customer_name)}</strong>{r.is_fund_request && !r.fund_request_released && (r.port_remarks || '').toUpperCase() !== 'RELEASED' && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginLeft: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}</span></CopyCell></td>
                      <td>
                        {editingWharfageId === r.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Select2Dropdown
                              options={wharfageEditOptions.map(o => ({ id: o, text: o }))}
                              value={tempWharfage}
                              onChange={setTempWharfage}
                              valueKey="id"
                              displayKey="text"
                              placeholder="Wharfage"
                              style={{ minWidth: 110 }}
                              clearable
                            />
                            <button
                              className="icon-btn save-btn"
                              title="Save"
                              disabled={savingWharfage}
                              onClick={() => saveWharfage(r)}
                            >
                              <i className="fa fa-save" />
                            </button>
                            <button
                              className="icon-btn cancel-btn"
                              title="Cancel"
                              onClick={cancelWharfage}
                            >
                              <i className="fa fa-times" />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CopyCell text={isPreverification(r.assessment_status) ? 'PRE-V' : r.wharfage_status}>
                              {isPreverification(r.assessment_status) ? (
                                <span style={{ color: 'red', fontWeight: 'bold' }}>PRE-V</span>
                              ) : (
                                wharfageBadge(r.wharfage_status, r.duty_paid, r.duty_paid_date)
                              )}
                            </CopyCell>
                            {hasPermission('PORT REPORTS', 'can_edit') && !fixedRemarkFilter && !isPreverification(r.assessment_status) && (
                              <button
                                type="button"
                                className="icon-btn"
                                title="Edit Wharfage"
                                onClick={() => startEditWharfage(r)}
                                style={{ padding: 4 }}
                              >
                                <i className="fa fa-pencil" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ userSelect: 'text' }} title={`BL: ${r.bl_no}`}><CopyCell text={r.bl_no}>{toUpperDisplay(r.bl_type)}</CopyCell></td>
                      <td className={isDropOffExpiringSoon(r.drop_off_expiry_date) && !isReleasedRow(r) ? 'flash-red' : ''} style={{ fontSize: 11, userSelect: 'text' }} title={isDropOffExpiringSoon(r.drop_off_expiry_date) && !isReleasedRow(r) ? 'Renew Dropoff' : `SL User: ${r.sl_user || 'N/A'}`}><CopyCell text={formatDoStatus(r.do_statuses, r.drop_off_expiry_date)}>{formatDoStatus(r.do_statuses, r.drop_off_expiry_date)}</CopyCell></td>
                      <td style={{ minWidth: 90, userSelect: 'text' }}
                          title={r.material || ''}>
                        <CopyCell text={r.material}>
                          <span style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {r.material && r.material.length > 20
                              ? toUpperDisplay(r.material.slice(0, 20)) + '...'
                              : toUpperDisplay(r.material)}
                          </span>
                        </CopyCell>
                      </td>
                      <td style={{ userSelect: 'text' }}><CopyCell text={(r.port_staff_names || []).join(', ')}>{toUpperDisplay((r.port_staff_names || []).join(', '))}</CopyCell></td>
                      <td style={{ userSelect: 'text' }}>
                        {r.port_transporters && r.port_transporters.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: '1.2em', fontSize: '12px' }}>
                                {r.port_transporters.map((t, i) => (
                                    <li key={i} title={t.tooltip || ''}>
                                        <CopyCell text={t.display_text}>{toUpperDisplay(t.display_text)}</CopyCell>
                                    </li>
                                ))}
                            </ul>
                        ) : '-'}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {editingScannerId === r.id ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="checkbox"
                              checked={tempScanner}
                              onChange={e => handleScannerChange(r, e.target.checked)}
                              disabled={savingScanner}
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelScanner}><i className="fa fa-times" /></button>
                          </div>
                        ) : (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {r.scanner_report_done ? (
                              <i className="fa fa-check" style={{ color: '#27ae60', fontSize: 16 }} title="Scanner report done" />
                            ) : (
                              <i className="fa fa-times" style={{ color: '#c0392b', fontSize: 16 }} title="Scanner report not done" />
                            )}
                            {hasPermission('PORT REPORTS', 'can_edit') && !fixedRemarkFilter && (
                              <button type="button" className="icon-btn" title="Edit Scanner Report" onClick={() => startEditScanner(r)} style={{ padding: 4 }}>
                                <i className="fa fa-pencil" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className={missingPortRemarks ? 'flash-yellow' : ''} title={missingPortRemarks ? 'Port Remarks required before generating the report' : undefined} style={{ minWidth: remarksColWidth, width: remarksColWidth, whiteSpace: fixedRemarkFilter === 'RELEASED' ? 'normal' : undefined, backgroundColor: r.remark_color || '#FFFFFF', color: getTextColorForBackground(r.remark_color || '#FFFFFF') }}>
                        {editingRemarksId === r.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Select2Dropdown
                                  options={getRemarkEditOptions(r)}
                                  value={tempRemarks}
                                  onChange={v => { setTempRemarks(v); if (v !== 'RELEASED') setTempReleaseDate(''); }}
                                  valueKey="id"
                                  displayKey="text"
                                  placeholder="Select Remark"
                                  menuPlacement={isLastRow ? 'top' : 'auto'}
                                  style={{ width: '100%' }}
                                  clearable
                                />
                              </div>
                              <button className="icon-btn save-btn" title="Save" disabled={savingRemarks} onClick={() => saveRemarks(r)}><i className="fa fa-save" /></button>
                              <button className="icon-btn cancel-btn" title="Cancel" onClick={cancelRemarks}><i className="fa fa-times" /></button>
                            </div>
                            {tempRemarks === 'RELEASED' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                                <input
                                  type="date"
                                  className="form-control"
                                  style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                                  value={tempReleaseDate}
                                  onChange={e => setTempReleaseDate(e.target.value)}
                                  required
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {fixedRemarkFilter === 'RELEASED' && r.port_remarks === 'RELEASED' ? (
                              <CopyCell text={getPortRemarksDisplay(r)}>
                                <span
                                  style={{ whiteSpace: 'pre-wrap', cursor: 'pointer' }}
                                  title={`RELEASED ON: ${r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : 'N/A'}`}
                                >
                                  {fundRequestIcon(r)}{getPortRemarksDisplay(r)}
                                </span>
                              </CopyCell>
                            ) : (
                              <CopyCell text={getPortRemarksDisplay(r)}>
                                <span
                                  style={{ whiteSpace: 'pre-wrap' }}
                                  title={r.port_remarks_original_value_date && r.port_remarks_mapped_label ? `${r.port_remarks_mapped_label}: ${new Date(r.port_remarks_original_value_date).toLocaleDateString('en-GB')}` : ''}
                                >{fundRequestIcon(r)}{getPortRemarksDisplay(r)}</span>
                              </CopyCell>
                            )}
                            {(fixedRemarkFilter === 'RELEASED' ? hasPermission('PORT REPORTS', 'can_delete') : hasPermission('PORT REPORTS', 'can_edit')) && (
                              <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditRemarks(r)}><i className="fa fa-pencil" /></button>
                            )}
                          </div>
                        )}
                      </td>
                      {fixedRemarkFilter === 'RELEASED' && (
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600, userSelect: 'text' }}>
                            <CopyCell text={r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : ''}>
                              {r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '—'}
                            </CopyCell>
                        </td>
                      )}
                    </tr>
                  );
                });
                return rowsWithSeparators;
              })()}
            </tbody>
          </table>
        </TableScrollContainer>

        {fixedRemarkFilter === 'RELEASED' && releasedTotalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8, fontSize: 13 }}>
            <span style={{ color: '#555' }}>
              {releasedStart + 1}–{Math.min(releasedStart + releasedItemsPerPage, releasedTotalCount)} of {releasedTotalCount}
            </span>
            <button onClick={() => handleReleasedPageChange(releasedCurrentPage - 1)} disabled={releasedCurrentPageClamped === 1}
              style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: releasedCurrentPageClamped === 1 ? 'default' : 'pointer', background: '#fff', color: releasedCurrentPageClamped === 1 ? '#bbb' : '#333' }}>
              ‹ Prev
            </button>
            <span style={{ color: '#555' }}>Page {releasedCurrentPageClamped} / {releasedTotalPages}</span>
            <button onClick={() => handleReleasedPageChange(releasedCurrentPage + 1)} disabled={releasedCurrentPageClamped === releasedTotalPages}
              style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: releasedCurrentPageClamped === releasedTotalPages ? 'default' : 'pointer', background: '#fff', color: releasedCurrentPageClamped === releasedTotalPages ? '#bbb' : '#333' }}>
              Next ›
            </button>
          </div>
        )}

        {containerDialogJob && (
          <ContainerDialog 
              job={containerDialogJob}
              transporters={dropdowns.transporters}
              lclTransporters={dropdowns.lcl_transporters}
              readOnly={!!fixedRemarkFilter}
              onClose={() => setContainerDialogJob(null)}
              onSaveSuccess={async () => {
                  const savedId = containerDialogJob.id;
                  setContainerDialogJob(null);
                  await fetchRows(true);
                  setFlashJobId(savedId);
              }}
              hasPermission={hasPermission} // 18. Pass permission down
          />
        )}
  
        {icdStaffDialog.open && (
          <IcdStaffDialog
              icdName={icdStaffDialog.icdName}
              staffList={dropdowns.staff}
              currentStaffIds={icdStaffDialog.staffIds}
              onClose={() => setIcdStaffDialog({ open: false, icdName: '', staffIds: [] })}
              onSaveSuccess={async () => {
                  const icdName = icdStaffDialog.icdName;
                  setIcdStaffDialog({ open: false, icdName: '', staffIds: [] });
                  await fetchRows(true);
                  setFlashIcd(icdName);
              }}
              hasPermission={hasPermission} // 19. Pass permission down
          />
        )}
  
        {showPortConfigModal && (
          <div className="modal-overlay">
              <div className="dialog-container" style={{ width: '80vw', maxWidth: '1200px' }}>
                  <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Port Configuration</h3>
                      <button className="icon-btn" onClick={() => { setShowPortConfigModal(false); fetchRows(); }} title="Close">
                          <i className="fa fa-times" />
                      </button>
                  </div>
                  <div className="dialog-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                      <PortConfiguration />
                  </div>
              </div>
          </div>
        )}
        <ConfirmDialog
          open={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />

        {jobFormModal.isOpen && (
          <div className="modal-overlay">
            <div className="dialog-container" style={{
              width: '95%',
              maxWidth: '1400px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div className="dialog-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3>Job Details (View Only)</h3>
                <button className="icon-btn" onClick={closeJobFormModal} title="Close">
                  <i className="fa fa-times" />
                </button>
              </div>
              <div className="dialog-body" style={{
                overflowY: 'auto',
                flex: 1
              }}>
                <JobForm
                  mode="view"
                  initialData={jobFormModal.job}
                  onClose={closeJobFormModal}
                  documentsEditable={false}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
};


export default function PortReport() {
    // --- FIX: Wrap hasPermission to allow ANY aliased permission that grants port report viewing ---
    const { hasPermission: rawHasPermission } = usePermissions();
    const hasPermission = useCallback((module, action) => {
        if (module === 'PORT REPORTS' || module === 'WHARFAGE_REPORT') {
            return rawHasPermission('PORT REPORTS', action) || rawHasPermission('ADMIN PORT REPORTS', action);
        }
        if (module === 'PORT CONFIG') { // Handling older name style just in case
            return rawHasPermission('PORT CONFIGURATIONS', action);
        }
        return rawHasPermission(module, action);
    }, [rawHasPermission]);
    const [activeTab, setActiveTab] = useState('portReport');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabCounts, setTabCounts] = useState({
        portReport: null,
        wharfageReport: null,
        releasedReport: null,
        dnRoReport: null,
        dailyPortReports: null,
    });

    const updateTabCount = useCallback((tab, count) => {
        setTabCounts(prev => ({ ...prev, [tab]: count }));
    }, []);

    // Fetch the pending DN/RO count on page load so the tab can flash immediately
    useEffect(() => {
        if (!hasPermission('PORT REPORTS', 'can_view')) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/port-report/?dn_ro_only=true&count_only=true`, { headers: getAuthHeaders(false) });
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) {
                    updateTabCount('dnRoReport', data.count ?? null);
                }
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { cancelled = true; };
    }, [hasPermission, updateTabCount]);

    const handleRefresh = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/port-report/`, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to fetch port report');
            let data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Set a default active tab based on permissions
        if (!hasPermission('PORT REPORTS', 'can_view') && hasPermission('WHARFAGE_REPORT', 'can_view')) {
            setActiveTab('wharfageReport');
        }
    }, [activeTab, hasPermission]);

    // 21. Add top-level permission check AFTER hooks
    if (!hasPermission('PORT REPORTS', 'can_view') && !hasPermission('WHARFAGE_REPORT', 'can_view')) {
        return (
            <div className="permission-denied-container" style={{ padding: '20px' }}>
                <h2>Permission Denied</h2>
                <p>You do not have permission to view any reports on this page.</p>
            </div>
        );
    }

    return (
        <div className="job-list-container port-report" style={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ margin: 0 }}>Port Reports</h2>
              <button onClick={handleRefresh} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
            </div>

            <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
                {/* 22. Conditionally render tabs */}
                {hasPermission('PORT REPORTS', 'can_view') && (
                    <button className={`custom-tab ${activeTab === 'portReport' ? 'active' : ''}`} onClick={() => { setRows([]); setLoading(true); setActiveTab('portReport'); }}>
                        Port Report {tabCounts.portReport !== null && `(${tabCounts.portReport})`}
                    </button>
                )}
                {hasPermission('PORT REPORTS', 'can_view') && (
                    <button className={`custom-tab ${activeTab === 'wharfageReport' ? 'active' : ''}`} onClick={() => setActiveTab('wharfageReport')}>
                        Wharfage Report {tabCounts.wharfageReport !== null && `(${tabCounts.wharfageReport})`}
                    </button>
                )}
                {hasPermission('PORT REPORTS', 'can_view') && (
                    <button className={`custom-tab ${activeTab === 'releasedReport' ? 'active' : ''}`} onClick={() => { setRows([]); setLoading(true); setActiveTab('releasedReport'); }}>
                        Released Report {tabCounts.releasedReport !== null && `(${tabCounts.releasedReport})`}
                    </button>
                )}
                {hasPermission('PORT REPORTS', 'can_view') && (
                    <button
                        className={`custom-tab ${activeTab === 'dnRoReport' ? 'active' : ''} ${tabCounts.dnRoReport ? 'flash-tab' : ''}`}
                        onClick={() => setActiveTab('dnRoReport')}
                    >
                        Pending DN/RO {tabCounts.dnRoReport !== null && `(${tabCounts.dnRoReport})`}
                    </button>
                )}
                <style>{`
                    @keyframes flash-tab {
                        0%, 100% { background-color: #e9f5ff; color: #333; box-shadow: 0 0 0 rgba(0, 0, 0, 0); }
                        50% { background-color: #003366; color: #fff; box-shadow: 0 0 10px rgba(0, 51, 102, 0.6); }
                    }
                    .custom-tab.flash-tab {
                        animation: flash-tab 1.2s ease-in-out infinite;
                        border-color: #003366;
                    }
                `}</style>
                {hasPermission('PORT REPORTS', 'can_view') && (
                  <button className={`custom-tab ${activeTab === 'dailyPortReports' ? 'active' : ''}`} onClick={() => setActiveTab('dailyPortReports')}>
                    Daily Port Reports {tabCounts.dailyPortReports !== null && `(${tabCounts.dailyPortReports})`}
                  </button>
                )}
            </div>

            <div className="tab-content">
                {activeTab === 'portReport' && hasPermission('PORT REPORTS', 'can_view') && (
                    <MainPortReportTab 
                        onRefresh={handleRefresh}
                        rows={rows}
                        setRows={setRows}
                        loading={loading}
                        setLoading={setLoading}
                        hasPermission={hasPermission} // 23. Pass permission down
                        onOpenDailyReports={() => setActiveTab('dailyPortReports')}
                        onCountChange={(count) => updateTabCount('portReport', count)}
                    />
                )}
                {activeTab === 'wharfageReport' && hasPermission('WHARFAGE_REPORT', 'can_view') && (
                    <WharfageReportTab hasPermission={hasPermission} onCountChange={(count) => updateTabCount('wharfageReport', count)} />
                )}
                {activeTab === 'releasedReport' && hasPermission('PORT REPORTS', 'can_view') && (
                    <MainPortReportTab 
                        onRefresh={handleRefresh}
                        rows={rows}
                        setRows={setRows}
                        loading={loading}
                        setLoading={setLoading}
                        hasPermission={hasPermission}
                        fixedRemarkFilter="RELEASED"
                        onOpenDailyReports={() => setActiveTab('dailyPortReports')}
                        onCountChange={(count) => updateTabCount('releasedReport', count)}
                    />
                )}
                    {activeTab === 'dailyPortReports' && hasPermission('PORT REPORTS', 'can_view') && (
                      <DailyPortReportsTab hasPermission={hasPermission} onCountChange={(count) => updateTabCount('dailyPortReports', count)} />
                    )}
                    {activeTab === 'dnRoReport' && hasPermission('PORT REPORTS', 'can_view') && (
                      <DnRoReportTab hasPermission={hasPermission} onCountChange={(count) => updateTabCount('dnRoReport', count)} />
                    )}
            </div>
        </div>
    );
}
